import React, { useMemo, useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { placeholderLabel } from "../../utility/placeholderLabel";
import HomeChargerImage from "resources/images/HomeChargerImage.png";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  handleSelectionChange,
  TextBoxSelection,
  TextBoxSelectionData,
} from "components/basic/textBoxSelection";
import AreaServicedSelector from "./components/AreaServicedSelector";
import WizardPageLayout from "../../components/WizardPageLayout";
import WizardSection from "../../components/WizardSection";
import WizardButton from "../../components/WizardButton";

const FORM_WIDTH = 320;

export interface ServiceOfferingData {
  supportedEVSETypes: TextBoxSelectionData[];
  buildingServiced: TextBoxSelectionData[];
  areaServiced: number;
}

interface ServiceOfferingSetupProps {
  data: ServiceOfferingData;
  setData: React.Dispatch<React.SetStateAction<ServiceOfferingData>>;
}

const ServiceOfferingSetup: React.FC<ServiceOfferingSetupProps> = ({
  data,
  setData,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation("services");
  const [searchParams] = useSearchParams();
  const originalDataRef = useRef<ServiceOfferingData | null>(null);

  // Detect if we're in edit mode
  const isEditMode = useMemo(
    () => !!searchParams.get("currentPage"),
    [searchParams],
  );

  // Store original data when entering edit mode
  useEffect(() => {
    if (isEditMode && !originalDataRef.current) {
      originalDataRef.current = JSON.parse(JSON.stringify(data));
    }
  }, [isEditMode, data]);

  const handleSave = useCallback(() => {
    // Data is already saved via setData callbacks as user selects options
    // Clear original data reference and navigate back
    originalDataRef.current = null;

    // Check if serviceId exists to determine which review page to return to
    const serviceId = searchParams.get("serviceId");
    if (serviceId) {
      navigate(`?currentPage=reviewAndUpdate&serviceId=${serviceId}`);
    } else {
      navigate("?currentPage=reviewAndLaunch");
    }
  }, [navigate, searchParams]);

  const handleCancel = useCallback(() => {
    // Revert to original data
    if (originalDataRef.current) {
      setData(originalDataRef.current);
      originalDataRef.current = null;
    }

    // Check if serviceId exists to determine which review page to return to
    const serviceId = searchParams.get("serviceId");
    if (serviceId) {
      navigate(`?currentPage=reviewAndUpdate&serviceId=${serviceId}`);
    } else {
      navigate("?currentPage=reviewAndLaunch");
    }
  }, [navigate, setData, searchParams]);

  return (
    <WizardPageLayout
      title={t("wizard.setup.title")}
      image={{
        src: HomeChargerImage,
        alt: t("wizard.image_alt"),
        side: "right",
      }}
      contentMaxWidth={FORM_WIDTH}
      actions={
        <>
          <WizardButton
            color="primary"
            onClick={
              isEditMode
                ? handleCancel
                : () => navigate("/dashboard/serviceOfferings")
            }
          >
            {t(isEditMode ? "wizard.cancel" : "wizard.back")}
          </WizardButton>

          <WizardButton
            color="warning"
            onClick={
              isEditMode
                ? handleSave
                : () => navigate("?currentPage=chargerConfig")
            }
          >
            {t(isEditMode ? "wizard.save" : "wizard.next")}
          </WizardButton>
        </>
      }
    >
      <WizardSection title={t("wizard.setup.evse_types")}>
        {data.supportedEVSETypes.map((element) => (
          <TextBoxSelection
            key={element.id}
            label={element.optionName}
            checked={element.isChecked}
            onSelectionChange={(checked) =>
              handleSelectionChange<TextBoxSelectionData>(
                setData,
                checked,
                "supportedEVSETypes",
                element.id,
              )
            }
            placeHolder={placeholderLabel(t, element.placeholder)}
          />
        ))}
      </WizardSection>

      <WizardSection title={t("wizard.setup.buildings")}>
        {data.buildingServiced.map((element) => (
          <TextBoxSelection
            key={element.id}
            textBoxSelectionType={element.type}
            label={element.optionName}
            checked={element.isChecked}
            onSelectionChange={(checked) =>
              handleSelectionChange<TextBoxSelectionData>(
                setData,
                checked,
                "buildingServiced",
                element.id,
              )
            }
            placeHolder={placeholderLabel(t, element.placeholder)}
          />
        ))}
      </WizardSection>

      <WizardSection title={t("wizard.setup.areas")}>
        <AreaServicedSelector
          value={data.areaServiced}
          onChange={(areaServiced) =>
            setData((prevData) => ({ ...prevData, areaServiced }))
          }
        />
      </WizardSection>
    </WizardPageLayout>
  );
};

export default ServiceOfferingSetup;
